import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import useToast from "components/atoms/toast/useToast";
import ManuscriptEditorPanel from "components/organisms/manuscript/ManuscriptEditorPanel";
import ManuscriptLeftPanel from "components/organisms/manuscript/ManuscriptLeftPanel";
import ManuscriptLeftRail from "components/organisms/manuscript/ManuscriptLeftRail";
import ManuscriptRightPanel from "components/organisms/manuscript/ManuscriptRightPanel";
import ManuscriptRightRail from "components/organisms/manuscript/ManuscriptRightRail";
import ReviewThreadDetail from "components/organisms/manuscript/overlay/ReviewThreadDetail";
import {
    buildCompileLogAnnotationsByNodeId,
    normalizeCompileLogPath,
    parseCompileLog,
} from "components/organisms/manuscript/compile/compileLog";
import {
    useLatestManuscriptCompile,
    useLatestSuccessfulManuscriptCompile,
    useManuscript,
    useManuscriptCompileCreate,
    useManuscriptDocument,
    useManuscriptHistory,
    useManuscriptHistoryRestore,
    useManuscriptReviewCreate,
    useManuscriptReviewMessageCreate,
    useManuscriptReviewReopen,
    useManuscriptReviewResolve,
    useManuscriptReviews,
    useManuscriptTree,
} from "hooks/queries/useManuscript";
import { useSpace } from "hooks/queries/useUser";
import { useManuscriptOTClient } from "hooks/useManuscriptOTClient";
import { historyOT, realtime } from "components/organisms/manuscript/adapter/ot";
import {
    createEmptySelectionActionSelection,
    createSelectionActionMenuItems,
    createSelectionActionMenuState,
} from "components/organisms/manuscript/adapter/selection-actions";
import {
    buildReviewOverlayPosition,
    createReviewComposerOpenState,
    createReviewOverlayClosedState,
    getReviewThreadAnchorRect,
    resolveReviewAnchor,
    resolveReviewThreadSelection,
    syncReviewThreadCommentCreated,
    syncReviewThreadCommentResolved,
    shouldDismissReviewOverlayOnSelectionChange,
} from "components/organisms/manuscript/adapter/review";
import {
    downloadManuscriptSourceZip,
    patchManuscript,
    deleteManuscriptNode,
    getManuscriptSearch,
    patchManuscriptSettings,
    patchManuscriptNode,
    postManuscriptNode,
} from "utils/request/manuscript";
import { MANUSCRIPT_COMPILER_OPTIONS } from "utils/constants/manuscript";

const HEADER_HEIGHT = 40;
const ENABLE_MANUSCRIPT_TRACK_CHANGES = false;
const TEXT_FILE_EXTENSIONS = new Set([
    "bib",
    "bst",
    "cls",
    "csv",
    "json",
    "md",
    "py",
    "r",
    "rb",
    "sh",
    "sty",
    "svg",
    "tex",
    "toml",
    "tsv",
    "txt",
    "xml",
    "yaml",
    "yml",
]);
const TEXT_MIME_TYPES = new Set([
    "application/javascript",
    "application/json",
    "application/ld+json",
    "application/sql",
    "application/typescript",
    "application/x-latex",
    "application/x-tex",
    "application/x-yaml",
    "application/xml",
    "image/svg+xml",
]);

function buildCompileRevisionMap(historyItems) {
    const items = Array.isArray(historyItems) ? historyItems : [];
    return items.reduce((map, item) => {
        if (item?.revision_type !== "compile_requested") {
            return map;
        }

        const jobId = item?.payload?.meta?.job_id;
        if (jobId == null) {
            return map;
        }

        map.set(Number(jobId), item?.revision_seq ?? null);
        return map;
    }, new Map());
}

function normalizeHistoryItems(historyData) {
    if (Array.isArray(historyData)) {
        return historyData;
    }
    if (Array.isArray(historyData?.results)) {
        return historyData.results;
    }
    return [];
}

function hashPresenceColor(value) {
    const palette = ["#2563eb", "#db2777", "#0f8f6f", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#4f46e5"];
    const source = String(value || "0");
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
        hash = (hash * 31 + source.charCodeAt(index)) >>> 0;
    }
    return palette[hash % palette.length];
}

function normalizePresenceParticipant(participant) {
    if (!participant) {
        return null;
    }
    const seed = participant.space_member_id || participant.user_id || participant.name || "0";
    return {
        ...participant,
        color: participant.color || hashPresenceColor(seed),
    };
}

function PresenceAvatar({ participant }) {
    const label = participant?.name || "Member";
    const fallback = String(label).trim().slice(0, 1).toUpperCase() || "M";
    const [imageFailed, setImageFailed] = useState(false);
    const imageUrl = participant?.image
        ? process.env.REACT_APP_ENV === "local"
            ? `http://${process.env.REACT_APP_API_BASE_URL}${participant.image}`
            : participant.image
        : "";

    useEffect(() => {
        setImageFailed(false);
    }, [imageUrl]);

    return (
        <PresenceAvatarWrap $background={participant?.color || "#3b82f6"} aria-label={label} title={label}>
            {imageUrl && !imageFailed ? (
                <PresenceAvatarImg src={imageUrl} alt={label} onError={() => setImageFailed(true)} />
            ) : (
                fallback
            )}
        </PresenceAvatarWrap>
    );
}

const getFileExtension = (name = "") => {
    const parts = String(name).toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() : "";
};

const isTextUploadFile = (file) => {
    const contentType = (file?.type || "").toLowerCase();
    if (contentType.startsWith("text/")) {
        return true;
    }
    if (TEXT_MIME_TYPES.has(contentType)) {
        return true;
    }
    return TEXT_FILE_EXTENSIONS.has(getFileExtension(file?.name));
};

const Page = styled.div`
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background: #dde6ef;
`;

const Header = styled.header`
    position: relative;
    z-index: 20;
    display: grid;
    grid-template-columns: minmax(320px, 1fr) minmax(220px, 420px) minmax(360px, 1fr);
    align-items: center;
    gap: 16px;
    height: ${HEADER_HEIGHT}px;
    padding: 0 14px;
    background: #f5f7fa;
    border-bottom: 1px solid #cfd8e3;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
`;

const HeaderBrand = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
`;

const HeaderLogoLink = styled(Link)`
    display: inline-flex;
    align-items: center;
`;

const ToolbarMenu = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    min-width: 0;
    overflow: hidden;
`;

const ToolbarMenuItem = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #32485e;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #e6edf5;
    }
`;

const HeaderCenter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderTitleWrap = styled.div`
    min-width: 0;
    text-align: center;
`;

const HeaderTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #162a3d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
`;

const PresenceGroup = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
`;

const PresenceAvatarWrap = styled.div`
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${(props) => props.$background || "#3b82f6"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 2px solid ${(props) => props.$background || "#3b82f6"};
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
`;

const PresenceAvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$primary ? "#147a63" : "#d4dee8")};
    border-radius: 6px;
    background: ${(props) => (props.$primary ? "#138a6d" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#32485e")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : "#eef3f7")};
    }
`;

const TitleMenuButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #d4dee8;
    border-radius: 6px;
    background: #ffffff;
    color: #41586f;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const HeaderTitleMenuWrap = styled.div`
    position: relative;
    flex: 0 0 auto;
`;

const TitleMenu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 196px;
    padding: 6px;
    border: 1px solid #d4dee8;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
`;

const TitleMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 34px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #32485e;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Workspace = styled.main`
    display: grid;
    grid-template-columns:
        40px
        ${(props) => (props.$leftPanelCollapsed ? "0px" : `${props.$leftPanelWidth}px`)}
        8px
        minmax(0, 1fr)
        8px
        ${(props) => (props.$rightPanelCollapsed ? "0px" : `${props.$rightPanelWidth}px`)}
        40px;
    gap: 0;
    height: calc(100vh - ${HEADER_HEIGHT}px);
    padding: 0;
    background: #eef3f7;

    @media (max-width: 1360px) {
        grid-template-columns:
            40px
            ${(props) => (props.$leftPanelCollapsed ? "0px" : `${Math.min(props.$leftPanelWidth, 320)}px`)}
            8px
            minmax(0, 1fr)
            8px
            ${(props) => (props.$rightPanelCollapsed ? "0px" : `${props.$rightPanelWidth}px`)}
            40px;
    }

    @media (max-width: 1100px) {
        grid-template-columns: 1fr;
        height: auto;
        min-height: calc(100vh - ${HEADER_HEIGHT}px);
    }
`;

const ResizeHandle = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e4ebf2;
    border-left: 1px solid #d5dee8;
    border-right: 1px solid #d5dee8;
    cursor: col-resize;

    &::after {
        content: "";
        position: absolute;
        left: 0;
        width: 100%;
        height: 18px;
        background:
            radial-gradient(circle, #9daabc 55%, transparent 56%) center 1px / 7px 5px repeat-y,
            radial-gradient(circle, #9daabc 55%, transparent 56%) center 12px / 7px 5px repeat-y;
        opacity: ${(props) => (props.$hidden ? 0.45 : 1)};
        pointer-events: none;
    }

    ${(props) =>
        props.$hidden &&
        `
        cursor: pointer;
        &::after {
            opacity: 0.7;
        }
    `}
`;

const ResizeHandleToggle = styled.button`
    position: absolute;
    top: calc(50% - 25px);
    left: 0;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: #8a96a8;
    color: #ffffff;
    cursor: pointer !important;
    font-size: 8px;
    font-weight: 800;
    line-height: 1;

    &:hover {
        background: #5b6a7c;
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const Status = styled.div`
    padding: 6px 10px;
    border-radius: 999px;
    background: ${(props) => props.$bg || "#eef2f7"};
    color: ${(props) => props.$color || "#29415d"};
    font-size: 12px;
    font-weight: 700;
`;

const TreeBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px 8px 14px 8px;
    overflow: auto;
`;

const TreeIndent = styled.div`
    margin-left: ${(props) => props.$depth * 12}px;
`;

const TreeItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-height: 28px;
    padding: 4px 8px;
    margin: 1px 0;
    border: 0;
    border-radius: 6px;
    background: ${(props) => (props.$selected ? "#dcecff" : "transparent")};
    color: ${(props) => (props.$selected ? "#0f4c81" : "#203249")};
    opacity: ${(props) => (props.$dragging ? 0.55 : 1)};
    cursor: pointer;
    text-align: left;

    &:hover {
        background: ${(props) => (props.$selected ? "#dcecff" : "#eef3f7")};
    }
`;

const TreeLabel = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 12px;
    font-weight: ${(props) => (props.$root ? 700 : 500)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TreeActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: 0;
    pointer-events: none;

    ${TreeItem}:hover &,
    ${TreeItem}[data-selected="true"] & {
        opacity: 1;
        pointer-events: auto;
    }
`;

const TreeActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 1px solid #d6e0ea;
    border-radius: 5px;
    background: #ffffff;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const TreeActionMenuWrap = styled.div`
    position: relative;
`;

const TreeActionMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 124px;
    padding: 4px;
    border: 1px solid #d6e0ea;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 20;
`;

const TreeActionMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #32485e;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const HelpDropdownMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 148px;
    padding: 4px;
    border: 1px solid #d6e0ea;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 30;
`;

const HelpDropdownItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #32485e;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const HeaderDropdownMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 180px;
    padding: 4px;
    border: 1px solid #d6e0ea;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 30;
`;

const HeaderDropdownItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #32485e;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 80;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.35);
`;

const Dialog = styled.div`
    width: min(520px, calc(100vw - 32px));
    max-height: calc(100vh - 32px);
    overflow: auto;
    border: 1px solid #d4dee8;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 20px 36px rgba(15, 23, 42, 0.2);
`;

const DialogHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    padding: 0 14px;
    border-bottom: 1px solid #e2e8f0;
    font-size: 14px;
    font-weight: 700;
    color: #1f3348;
`;

const DialogBody = styled.div`
    padding: 10px;
`;

const ShortcutTable = styled.div`
    display: grid;
    grid-template-columns: minmax(120px, 160px) minmax(110px, 150px) minmax(0, 1fr);
    border: 1px solid #dbe5ef;
    border-radius: 8px;
    overflow: hidden;
`;

const ShortcutCell = styled.div`
    padding: 8px 10px;
    border-bottom: 1px solid #e6edf5;
    border-right: ${(props) => (props.$last ? "0" : "1px solid #e6edf5")};
    background: ${(props) => (props.$header ? "#f4f8fc" : "#ffffff")};
    font-size: 12px;
    font-weight: ${(props) => (props.$header ? 700 : 500)};
    color: #2f455c;
`;

const SettingsTabs = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0 10px 0;
`;

const SettingsTabButton = styled.button`
    height: 30px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "#0f8f6f" : "#d6e0ea")};
    border-radius: 7px;
    background: ${(props) => (props.$active ? "#e7faf3" : "#ffffff")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#32485e")};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

const SettingsForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SettingsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SettingsLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #253a50;
`;

const SettingsSelect = styled.select`
    height: 34px;
    padding: 0 10px;
    border: 1px solid #d5dfea;
    border-radius: 8px;
    background: #ffffff;
    color: #24384d;
    font-size: 13px;
`;

const SettingsHint = styled.div`
    font-size: 11px;
    color: #5d7389;
`;

const ReviewOverlayShell = styled.div`
    position: fixed;
    z-index: 60;
    width: min(360px, calc(100vw - 24px));
    max-height: min(440px, calc(100vh - 24px));
    padding: 10px;
    border: 1px solid #cfe0d7;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 18px 40px rgba(15, 76, 129, 0.18);
    backdrop-filter: blur(10px);
    overflow: auto;
`;

const EditorContextMenu = styled.div`
    position: fixed;
    z-index: 80;
    min-width: 140px;
    padding: 6px;
    border: 1px solid #d6e0eb;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
`;

const EditorContextMenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #23384d;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    text-align: left;

    &:hover {
        background: #eef4f8;
    }
`;

function buildTree(nodes) {
    const nodeMap = new Map((nodes || []).map((node) => [node.id, { ...node, children: [] }]));
    const roots = [];
    nodeMap.forEach((node) => {
        if (node.parent_id && nodeMap.has(node.parent_id)) {
            nodeMap.get(node.parent_id).children.push(node);
        } else {
            roots.push(node);
        }
    });
    const sortNodes = (items) => {
        items.sort((left, right) => {
            if (left.node_type === "folder" && right.node_type !== "folder") return -1;
            if (left.node_type !== "folder" && right.node_type === "folder") return 1;
            return left.name.localeCompare(right.name);
        });
        items.forEach((item) => sortNodes(item.children));
    };
    sortNodes(roots);
    return { roots, nodeMap };
}

function ancestorFolderIds(node, nodeMap) {
    const ids = [];
    let current = node?.parent_id ? nodeMap.get(node.parent_id) : null;
    while (current) {
        ids.push(current.id);
        current = current.parent_id ? nodeMap.get(current.parent_id) : null;
    }
    return ids;
}

function caretInfo(text, position) {
    const lines = (text || "").slice(0, Math.max(0, position || 0)).split("\n");
    return {
        line: lines.length,
        column: lines[lines.length - 1].length + 1,
    };
}

const OUTLINE_COMMAND_LEVELS = {
    part: 0,
    chapter: 0,
    section: 1,
    subsection: 2,
    subsubsection: 3,
    paragraph: 4,
    subparagraph: 5,
};

function extractLatexOutline(content) {
    const lines = (content || "").split("\n");
    const roots = [];
    const stack = [];

    lines.forEach((line, index) => {
        const match = line.match(
            /\\(part|chapter|section|subsection|subsubsection|paragraph|subparagraph)\*?\{([^}]*)\}/,
        );
        if (!match) return;
        const [, command, title] = match;
        const item = {
            id: `${command}-${index + 1}`,
            command,
            title: title.trim() || command,
            line: index + 1,
            level: OUTLINE_COMMAND_LEVELS[command] ?? 1,
            children: [],
        };

        while (stack.length && stack[stack.length - 1].level >= item.level) {
            stack.pop();
        }

        if (stack.length) {
            stack[stack.length - 1].children.push(item);
        } else {
            roots.push(item);
        }

        stack.push(item);
    });

    return roots;
}

function findActiveOutlinePath(items, line, path = []) {
    let bestCandidate = {
        path: [],
        line: -1,
    };

    items.forEach((item) => {
        if (item.line > line) return;
        const nextPath = [...path, item];
        const childCandidate = findActiveOutlinePath(item.children || [], line, nextPath);
        const candidate = childCandidate.line >= 0 ? childCandidate : { path: nextPath, line: item.line };

        if (
            candidate.line > bestCandidate.line ||
            (candidate.line === bestCandidate.line && candidate.path.length > bestCandidate.path.length)
        ) {
            bestCandidate = candidate;
        }
    });

    return bestCandidate;
}

function getThreadCreationAnchorRange(thread) {
    return thread?.creation_anchor_range || null;
}

function getThreadAnchorPath(thread) {
    return thread?.anchor_path || thread?.anchor_node?.path_cache || "";
}

function normalizeReviewThread(thread) {
    if (!thread) {
        return thread;
    }
    const creationAnchorRange = getThreadCreationAnchorRange(thread);
    return {
        ...thread,
        id: thread.id == null ? thread.id : String(thread.id),
        creation_anchor_range: creationAnchorRange,
        anchor_path: getThreadAnchorPath(thread),
    };
}

function getReviewThreadRange(thread) {
    const anchor = getThreadCreationAnchorRange(thread) || {};
    if (thread?.anchor_type !== "document_range" || !anchor.start_line || !anchor.start_col || !anchor.end_col) {
        return null;
    }
    return {
        line: anchor.start_line,
        startCol: anchor.start_col,
        endCol: anchor.end_col,
    };
}

function ManuscriptEditorPage() {
    const { setToast } = useToast();
    const queryClient = useQueryClient();
    const { space_uuid, manuscriptId } = useParams();

    const manuscriptQuery = useManuscript(manuscriptId);
    const spaceQuery = useSpace();
    const treeQuery = useManuscriptTree(manuscriptId);
    const latestCompileQuery = useLatestManuscriptCompile(manuscriptId, {
        refetchInterval: (data) => {
            if (!data) {
                return false;
            }
            return ["queued", "running"].includes(data.status) ? 2500 : false;
        },
    });
    const latestSuccessfulCompileQuery = useLatestSuccessfulManuscriptCompile(manuscriptId);
    const compileMutation = useManuscriptCompileCreate();
    const renameManuscriptMutation = useMutation((payload) => patchManuscript(space_uuid, payload), {
        onSuccess: (data) => {
            queryClient.setQueryData(["manuscript", space_uuid, manuscriptId], data);
            queryClient.invalidateQueries(["manuscripts", space_uuid]);
        },
    });
    const historyQuery = useManuscriptHistory(manuscriptId);
    const reviewQuery = useManuscriptReviews(manuscriptId);
    const restoreMutation = useManuscriptHistoryRestore();
    const reviewCreateMutation = useManuscriptReviewCreate();
    const reviewMessageMutation = useManuscriptReviewMessageCreate();
    const reviewResolveMutation = useManuscriptReviewResolve();
    const reviewReopenMutation = useManuscriptReviewReopen();
    const treeNodes = treeQuery.data || [];
    const { roots, nodeMap } = useMemo(() => buildTree(treeNodes), [treeNodes]);

    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expandedFolderIds, setExpandedFolderIds] = useState({});
    const [leftPanelTab, setLeftPanelTab] = useState("files");
    const [rightPanelTab, setRightPanelTab] = useState("pdf");
    const [editorContent, setEditorContent] = useState("");
    const [currentEditorMode, setCurrentEditorMode] = useState("visual");
    const [currentVersion, setCurrentVersion] = useState(null);
    const [saveState, setSaveState] = useState("idle");
    const [isDirty, setIsDirty] = useState(false);
    const [selectedReviewThreadId, setSelectedReviewThreadId] = useState(null);
    const [pendingReviewRangeFocus, setPendingReviewRangeFocus] = useState(null);
    const [pendingLintErrorFocus, setPendingLintErrorFocus] = useState(null);
    const [reviewReplyDraft, setReviewReplyDraft] = useState("");
    const [reviewOverlayPosition, setReviewOverlayPosition] = useState(null);
    const [reviewOverlayTick, setReviewOverlayTick] = useState(0);
    const [reviewComposerAnchor, setReviewComposerAnchor] = useState(null);
    const [reviewComposerRequestKey, setReviewComposerRequestKey] = useState(0);
    const [selectionActionMenuState, setSelectionActionMenuState] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [compileLogRaw, setCompileLogRaw] = useState("");
    const [compileLogLoading, setCompileLogLoading] = useState(false);
    const [compileLogLoadError, setCompileLogLoadError] = useState("");
    const [hasLintingError, setHasLintingError] = useState(false);
    const [firstLintError, setFirstLintError] = useState(null);
    const [selection, setSelection] = useState(createEmptySelectionActionSelection());
    const [presenceParticipants, setPresenceParticipants] = useState([]);
    const [remoteCursors, setRemoteCursors] = useState({});
    const [activeEditorLine, setActiveEditorLine] = useState(1);
    const [leftPanelWidth, setLeftPanelWidth] = useState(300);
    const [rightPanelWidth, setRightPanelWidth] = useState(360);
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
    const [isTitleMenuOpen, setIsTitleMenuOpen] = useState(false);
    const [isShortcutModalOpen, setIsShortcutModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [settingsTab, setSettingsTab] = useState("compiler");
    const [helpMenuPosition, setHelpMenuPosition] = useState(null);
    const [headerMenuState, setHeaderMenuState] = useState(null);
    const [settingsRootNodeId, setSettingsRootNodeId] = useState("");
    const [settingsCompiler, setSettingsCompiler] = useState("xelatex");
    const [treeActionMenuNodeId, setTreeActionMenuNodeId] = useState(null);
    const [treeActionMenuPosition, setTreeActionMenuPosition] = useState(null);
    const [draggedTreeNodeId, setDraggedTreeNodeId] = useState(null);
    const [dropTargetNodeId, setDropTargetNodeId] = useState(null);
    const [editorCommand, setEditorCommand] = useState(null);

    const latestContentRef = useRef("");
    const syncedContentRef = useRef("");
    const currentVersionRef = useRef(null);
    const isDirtyRef = useRef(false);
    const fileInputRef = useRef(null);
    const dragStateRef = useRef(null);
    const titleMenuRef = useRef(null);
    const treeActionMenuRef = useRef(null);
    const helpMenuRef = useRef(null);
    const headerMenuRef = useRef(null);
    const codeMirrorRef = useRef(null);
    const reviewOverlayRef = useRef(null);
    const reviewThreadAnchorElementMapRef = useRef(new Map());
    const editorReviewContextMenuRef = useRef(null);
    const suppressReviewOverlayDismissRef = useRef(false);
    const searchRunRef = useRef(0);
    const preferredEditorModesRef = useRef({});
    const cursorBroadcastTimerRef = useRef(null);
    const pendingCursorPayloadRef = useRef(null);
    const isMac = useMemo(() => /Mac|iPhone|iPad|iPod/i.test(window.navigator.platform || ""), []);

    const selectedNode = selectedNodeId ? nodeMap.get(Number(selectedNodeId)) : null;
    const selectedTextNodeId = selectedNode?.node_type === "text" ? selectedNode.id : null;
    const documentQuery = useManuscriptDocument(manuscriptId, selectedTextNodeId);

    const documentOutline = useMemo(() => extractLatexOutline(editorContent), [editorContent]);
    const activeOutlinePath = useMemo(() => {
        const result = findActiveOutlinePath(documentOutline, activeEditorLine);
        return result.path;
    }, [documentOutline, activeEditorLine]);
    const activeOutlineIds = useMemo(() => activeOutlinePath.map((item) => item.id), [activeOutlinePath]);
    const latestCompile = latestCompileQuery.data;
    const latestSuccessfulCompile = latestSuccessfulCompileQuery.data;
    const latestSuccessfulPdfArtifact = latestSuccessfulCompile?.artifacts?.find(
        (artifact) => artifact.artifact_kind === "pdf",
    );
    const logArtifact = latestCompile?.artifacts?.find((artifact) => artifact.artifact_kind === "log");
    const compileLogEntries = useMemo(() => parseCompileLog(compileLogRaw), [compileLogRaw]);
    const compileLogAnnotationsByNodeId = useMemo(
        () => buildCompileLogAnnotationsByNodeId(compileLogEntries, treeNodes),
        [compileLogEntries, treeNodes],
    );
    const historyItems = useMemo(() => normalizeHistoryItems(historyQuery.data), [historyQuery.data]);
    const compileRevisionMap = useMemo(() => buildCompileRevisionMap(historyItems), [historyItems]);
    const latestCompileRevisionSeq =
        latestCompile?.id != null ? (compileRevisionMap.get(Number(latestCompile.id)) ?? null) : null;
    const compileInProgress =
        compileMutation.isLoading || ["queued", "running"].includes(String(latestCompile?.status || ""));
    const compileDisabledReason = !selectedTextNodeId ? "컴파일할 텍스트 문서를 선택하세요." : "";
    const displayedPdfArtifact = latestSuccessfulPdfArtifact || null;
    const displayedPdfRevisionSeq =
        latestSuccessfulCompile?.id != null
            ? (compileRevisionMap.get(Number(latestSuccessfulCompile.id)) ?? null)
            : null;
    const reviewThreads = useMemo(
        () => (Array.isArray(reviewQuery.data) ? reviewQuery.data.map(normalizeReviewThread) : []),
        [reviewQuery.data],
    );
    const compilerOptions = MANUSCRIPT_COMPILER_OPTIONS;
    const rootCandidateNodes = useMemo(
        () => treeNodes.filter((node) => node.node_type === "text" && (node.name || "").toLowerCase().endsWith(".tex")),
        [treeNodes],
    );
    const currentRootNode = useMemo(
        () => treeNodes.find((node) => node.id === manuscriptQuery.data?.root_tree_node_id) || null,
        [manuscriptQuery.data?.root_tree_node_id, treeNodes],
    );
    const currentReviewAnchor = resolveReviewAnchor({
        selectedNode,
        selection,
        editorView: codeMirrorRef.current?.view,
    });
    const selectedReviewThread = useMemo(
        () => reviewThreads.find((thread) => thread.id === selectedReviewThreadId) || null,
        [reviewThreads, selectedReviewThreadId],
    );
    const mySpaceMemberId = useMemo(() => {
        const spaces = Array.isArray(spaceQuery.data) ? spaceQuery.data : [];
        return spaces.find((space) => space.uuid === space_uuid)?.my_member_info?.id ?? null;
    }, [spaceQuery.data, space_uuid]);
    const activePresenceParticipants = useMemo(
        () => presenceParticipants.filter((participant) => participant?.space_member_id || participant?.user_id),
        [presenceParticipants],
    );
    const activeRemoteCursors = useMemo(
        () =>
            Object.values(remoteCursors).filter(
                (cursor) =>
                    cursor &&
                    Number(cursor.node_id) === Number(selectedTextNodeId) &&
                    cursor.participant?.space_member_id !== mySpaceMemberId,
            ),
        [mySpaceMemberId, remoteCursors, selectedTextNodeId],
    );

    const syncDocumentState = (document, force = false) => {
        if (!document) return;
        const content = document.current_content || "";
        const availableModes = document.available_editor_modes || ["code"];
        const preferredMode = preferredEditorModesRef.current[document.node_id];
        const defaultMode = availableModes.includes("visual")
            ? "visual"
            : document.editor_mode || availableModes[0] || "code";
        const nextEditorMode = preferredMode && availableModes.includes(preferredMode) ? preferredMode : defaultMode;
        syncedContentRef.current = content;
        currentVersionRef.current = document.current_version;
        setCurrentEditorMode(nextEditorMode);
        setCurrentVersion(document.current_version);
        if (force || !isDirtyRef.current) {
            latestContentRef.current = content;
            setEditorContent(content);
            setIsDirty(false);
            isDirtyRef.current = false;
            setSaveState("saved");
            return;
        }
        const dirty = latestContentRef.current !== syncedContentRef.current;
        setIsDirty(dirty);
        isDirtyRef.current = dirty;
        setSaveState(dirty ? "dirty" : "saved");
    };

    const nodeMutation = useMutation(
        async ({ type, nodeId, payload }) => {
            if (type === "create") return postManuscriptNode(space_uuid, manuscriptId, payload);
            if (type === "update") return patchManuscriptNode(space_uuid, manuscriptId, nodeId, payload);
            return deleteManuscriptNode(space_uuid, manuscriptId, nodeId);
        },
        {
            onSuccess: () => {
                treeQuery.refetch();
                manuscriptQuery.refetch();
            },
        },
    );

    const manuscriptSettingsMutation = useMutation(
        (payload) => patchManuscriptSettings(space_uuid, manuscriptId, payload),
        {
            onSuccess: (data) => {
                queryClient.setQueryData(["manuscript", space_uuid, manuscriptId], data);
                manuscriptQuery.refetch();
            },
        },
    );

    useEffect(() => {
        currentVersionRef.current = currentVersion;
    }, [currentVersion]);

    useEffect(() => {
        const rootNodeId = manuscriptQuery.data?.root_tree_node_id;
        if (rootNodeId != null) {
            setSettingsRootNodeId(String(rootNodeId));
        }
        const compiler = manuscriptQuery.data?.default_compiler;
        if (compiler) {
            setSettingsCompiler(compiler);
        }
    }, [manuscriptQuery.data?.root_tree_node_id, manuscriptQuery.data?.default_compiler]);

    useEffect(() => {
        if (!rootCandidateNodes.length) {
            setSettingsRootNodeId("");
            return;
        }
        const exists = rootCandidateNodes.some((node) => String(node.id) === settingsRootNodeId);
        if (!exists) {
            setSettingsRootNodeId(String(rootCandidateNodes[0].id));
        }
    }, [rootCandidateNodes, settingsRootNodeId]);

    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        setSelection(createEmptySelectionActionSelection());
        setActiveEditorLine(1);
        setPresenceParticipants([]);
        setRemoteCursors({});
        setHasLintingError(false);
        setFirstLintError(null);
        setPendingLintErrorFocus(null);
        pendingCursorPayloadRef.current = null;
        if (cursorBroadcastTimerRef.current) {
            window.clearTimeout(cursorBroadcastTimerRef.current);
            cursorBroadcastTimerRef.current = null;
        }
    }, [selectedTextNodeId]);

    useEffect(() => {
        return () => {
            if (cursorBroadcastTimerRef.current) {
                window.clearTimeout(cursorBroadcastTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleEditorLint = (event) => {
            setHasLintingError(Boolean(event?.detail?.hasLintingError));
            setFirstLintError(event?.detail?.firstError || null);
        };

        window.addEventListener("editor:lint", handleEditorLint);
        return () => {
            window.removeEventListener("editor:lint", handleEditorLint);
        };
    }, []);

    useEffect(() => {
        if (!reviewThreads.length) {
            setSelectedReviewThreadId(null);
            return;
        }
        if (selectedReviewThreadId == null) {
            return;
        }
        const exists = reviewThreads.some((thread) => thread.id === selectedReviewThreadId);
        if (!exists) {
            setSelectedReviewThreadId(null);
        }
    }, [reviewThreads, selectedReviewThreadId]);

    useEffect(() => {
        setReviewReplyDraft("");
    }, [selectedReviewThreadId]);

    useEffect(() => {
        if (suppressReviewOverlayDismissRef.current) {
            suppressReviewOverlayDismissRef.current = false;
            return;
        }

        const shouldDismiss = shouldDismissReviewOverlayOnSelectionChange({
            selectedThread: selectedReviewThread,
            selectedNodeId: selectedTextNodeId,
            selection,
        });
        if (shouldDismiss) {
            const closedState = createReviewOverlayClosedState();
            setSelectedReviewThreadId(closedState.selectedReviewThreadId);
            setReviewReplyDraft(closedState.reviewReplyDraft);
            setReviewOverlayPosition(closedState.reviewOverlayPosition);
        }
    }, [selectedReviewThread, selectedTextNodeId, selection]);

    useEffect(() => {
        if (!pendingReviewRangeFocus) return;
        if (!selectedTextNodeId) return;
        if (Number(selectedTextNodeId) !== Number(pendingReviewRangeFocus.nodeId)) return;
        if (!documentQuery.data || Number(documentQuery.data.node_id) !== Number(pendingReviewRangeFocus.nodeId))
            return;

        jumpToLine(pendingReviewRangeFocus.line, {
            startCol: pendingReviewRangeFocus.startCol,
            endCol: pendingReviewRangeFocus.endCol,
        });
        setPendingReviewRangeFocus(null);
    }, [documentQuery.data, pendingReviewRangeFocus, selectedTextNodeId]);

    useEffect(() => {
        if (!pendingLintErrorFocus) return;
        if (currentEditorMode !== "code") return;
        if (!selectedTextNodeId) return;
        if (Number(selectedTextNodeId) !== Number(pendingLintErrorFocus.nodeId)) return;
        if (!documentQuery.data || Number(documentQuery.data.node_id) !== Number(pendingLintErrorFocus.nodeId)) return;
        if (!codeMirrorRef.current?.view) return;

        jumpToLine(pendingLintErrorFocus.line, {
            startCol: pendingLintErrorFocus.startCol,
            endCol: pendingLintErrorFocus.endCol,
        });
        setPendingLintErrorFocus(null);
    }, [currentEditorMode, documentQuery.data, pendingLintErrorFocus, selectedTextNodeId]);

    useEffect(() => {
        if (!selectedReviewThread || rightPanelTab !== "review" || isRightPanelCollapsed) {
            setReviewOverlayPosition(null);
            return;
        }

        updateReviewOverlayPosition();
        const animationFrameId = window.requestAnimationFrame(() => {
            updateReviewOverlayPosition();
        });

        const handleViewportChange = () => {
            updateReviewOverlayPosition();
        };

        window.addEventListener("resize", handleViewportChange);
        window.addEventListener("scroll", handleViewportChange, true);
        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [
        selectedReviewThread,
        selectedTextNodeId,
        activeEditorLine,
        rightPanelTab,
        isRightPanelCollapsed,
        treeNodes,
        documentQuery.data,
        reviewOverlayTick,
    ]);

    useEffect(() => {
        if (!selectedReviewThread || !reviewOverlayRef.current || typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            setReviewOverlayTick((prev) => prev + 1);
        });
        observer.observe(reviewOverlayRef.current);
        return () => observer.disconnect();
    }, [selectedReviewThread]);

    useEffect(() => {
        if (!treeNodes.length) return;
        if (selectedNodeId && nodeMap.has(Number(selectedNodeId))) return;
        const defaultNode =
            (manuscriptQuery.data?.root_tree_node_id && nodeMap.get(manuscriptQuery.data.root_tree_node_id)) ||
            treeNodes.find((node) => node.node_type === "text") ||
            treeNodes[0];
        if (!defaultNode) return;
        setSelectedNodeId(defaultNode.id);
        const ancestors = ancestorFolderIds(defaultNode, nodeMap);
        if (ancestors.length) {
            setExpandedFolderIds((prev) => {
                const next = { ...prev };
                ancestors.forEach((id) => {
                    next[id] = true;
                });
                return next;
            });
        }
    }, [manuscriptQuery.data?.root_tree_node_id, nodeMap, selectedNodeId, treeNodes]);

    const {
        currentDoc,
        isConnected,
        isReady: isOTReady,
        sendCursorUpdate,
    } = useManuscriptOTClient({
        spaceUuid: space_uuid,
        manuscriptId,
        nodeId: selectedTextNodeId,
        enabled: !!selectedTextNodeId,
        initialDocument: documentQuery.data,
        trackChangesUserId: ENABLE_MANUSCRIPT_TRACK_CHANGES && mySpaceMemberId ? String(mySpaceMemberId) : null,
        onDocumentState: (document) => {
            if (document?.node_id !== selectedTextNodeId) return;
            queryClient.setQueryData(["manuscriptDocument", space_uuid, manuscriptId, document.node_id], document);
            syncDocumentState(document, currentVersionRef.current == null);
        },
        onPresenceEvent: (payload) => {
            if ((payload?.event || "") !== "snapshot") {
                return;
            }
            const participants = Array.isArray(payload?.participants)
                ? payload.participants.map(normalizePresenceParticipant).filter(Boolean)
                : [];
            setPresenceParticipants(participants);
            setRemoteCursors((current) => {
                const activeIds = new Set(
                    participants.map((participant) => String(participant.space_member_id || participant.user_id || "")),
                );
                return Object.fromEntries(Object.entries(current).filter(([key]) => activeIds.has(String(key))));
            });
        },
        onCursorEvent: ({ payload, user_id: userId }) => {
            const participant = normalizePresenceParticipant(payload?.participant || { user_id: userId });
            const participantId = participant?.space_member_id || participant?.user_id;
            if (!participantId) {
                return;
            }
            setRemoteCursors((current) => ({
                ...current,
                [participantId]: {
                    ...payload,
                    participant,
                    color: participant.color,
                },
            }));
        },
        onSocketError: (error) => {
            console.error(error);
            setSaveState("error");
        },
    });

    useEffect(() => {
        if (!documentQuery.data) {
            return;
        }

        const hasLiveOtSession =
            currentDoc &&
            Number(documentQuery.data.node_id) === Number(selectedTextNodeId) &&
            currentDoc.getVersion() != null;

        syncDocumentState(documentQuery.data, !hasLiveOtSession);
    }, [currentDoc, documentQuery.data, selectedTextNodeId]);

    useEffect(() => {
        if (selectedNode?.node_type !== "text") {
            setCurrentEditorMode("code");
        }
    }, [selectedNode]);

    useEffect(() => {
        if (rightPanelTab !== "review" || isRightPanelCollapsed) {
            return;
        }
        reviewQuery.refetch();
    }, [isRightPanelCollapsed, rightPanelTab]);

    useEffect(() => {
        if (latestCompile?.status !== "succeeded") return;
        setRightPanelTab("pdf");
        setIsRightPanelCollapsed(false);
        latestSuccessfulCompileQuery.refetch();
    }, [latestCompile?.id, latestCompile?.status]);

    useEffect(() => {
        if (!logArtifact?.file_url || ["queued", "running"].includes(latestCompile?.status || "")) {
            setCompileLogRaw("");
            setCompileLogLoadError("");
            setCompileLogLoading(false);
            return undefined;
        }

        const controller = new AbortController();

        const loadCompileLog = async () => {
            setCompileLogLoading(true);
            setCompileLogLoadError("");
            try {
                const response = await fetch(logArtifact.file_url, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`LOG fetch failed: ${response.status}`);
                }
                const text = await response.text();
                if (!controller.signal.aborted) {
                    setCompileLogRaw(text);
                }
            } catch (_error) {
                if (!controller.signal.aborted) {
                    setCompileLogRaw("");
                    setCompileLogLoadError("로그를 불러오지 못했습니다.");
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCompileLogLoading(false);
                }
            }
        };

        loadCompileLog();
        return () => controller.abort();
    }, [latestCompile?.id, latestCompile?.status, logArtifact?.file_url]);

    const flushCurrentDocument = async () => {
        if (!selectedTextNodeId) {
            return true;
        }

        if (!currentDoc || !currentDoc.hasBufferedOps()) {
            setSaveState("saved");
            return true;
        }

        try {
            setSaveState("saving");
            currentDoc.flush();
            await new Promise((resolve) => {
                const startedAt = Date.now();

                const poll = () => {
                    if (!currentDoc.hasBufferedOps()) {
                        setSaveState("saved");
                        resolve();
                        return;
                    }

                    if (Date.now() - startedAt >= 1200) {
                        resolve();
                        return;
                    }

                    window.setTimeout(poll, 50);
                };

                poll();
            });
            return true;
        } catch (error) {
            setSaveState("error");
            Alert("warn", "에러", "문서 저장 중 오류가 발생했습니다.");
            return false;
        }
    };

    const otSnapshotText = useMemo(() => {
        if (!currentDoc || !selectedTextNodeId || !isOTReady) {
            return null;
        }
        return currentDoc.getSnapshot();
    }, [currentDoc, currentVersion, editorContent, isOTReady, selectedTextNodeId]);

    const isRealtimeBootstrapReady = useMemo(() => {
        if (!currentDoc || !selectedTextNodeId || !isOTReady) {
            return false;
        }
        if (typeof otSnapshotText !== "string") {
            return false;
        }
        return editorContent === otSnapshotText;
    }, [currentDoc, editorContent, isOTReady, otSnapshotText, selectedTextNodeId]);

    const realtimeExtension = useMemo(() => {
        if (!currentDoc || !selectedTextNodeId || !isOTReady || !isRealtimeBootstrapReady) {
            return null;
        }

        return [
            realtime({ currentDoc }, (error) => {
                console.error(error);
                setSaveState("error");
            }),
            historyOT(currentDoc),
        ];
    }, [currentDoc, isOTReady, isRealtimeBootstrapReady, selectedTextNodeId]);

    useEffect(() => {
        if (!currentDoc || !selectedTextNodeId) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            const saved = currentDoc.pollSavedStatus();
            setSaveState((current) => {
                if (current === "error") {
                    return current;
                }
                if (saved) {
                    return isDirtyRef.current ? "dirty" : "saved";
                }
                return "saving";
            });
        }, 400);

        return () => window.clearInterval(timer);
    }, [currentDoc, selectedTextNodeId]);

    const openNode = async (node) => {
        if (!node) return;
        if (selectedTextNodeId && selectedTextNodeId !== node.id) {
            await flushCurrentDocument();
        }
        if (node.node_type === "folder") {
            setExpandedFolderIds((prev) => ({ ...prev, [node.id]: !prev[node.id] }));
            setSelectedNodeId(node.id);
            return;
        }
        setSelectedNodeId(node.id);
        const ancestors = ancestorFolderIds(node, nodeMap);
        if (ancestors.length) {
            setExpandedFolderIds((prev) => {
                const next = { ...prev };
                ancestors.forEach((id) => {
                    next[id] = true;
                });
                return next;
            });
        }
    };

    const createNode = async (nodeType) => {
        const name = window.prompt(
            nodeType === "folder" ? "새 폴더 이름을 입력하세요." : "새 파일 이름을 입력하세요.",
            nodeType === "folder" ? "sections" : "section.tex",
        );
        if (!name) return;
        const parentId = selectedNode?.node_type === "folder" ? selectedNode.id : selectedNode?.parent_id || null;
        try {
            const node = await nodeMutation.mutateAsync({
                type: "create",
                payload: {
                    parent_id: parentId,
                    node_type: nodeType,
                    name,
                    current_content: nodeType === "text" ? "" : undefined,
                },
            });
            if (nodeType === "text") {
                setSelectedNodeId(node.id);
            }
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.name?.[0] || "노드 생성 중 오류가 발생했습니다.");
        }
    };

    const setRootNode = async (node) => {
        if (!node || node.node_type !== "text") return;
        try {
            await manuscriptSettingsMutation.mutateAsync({
                root_tree_node_id: node.id,
                default_compiler: manuscriptQuery.data?.default_compiler || settingsCompiler,
            });
            await treeQuery.refetch();
            setToast("루트 문서를 변경했습니다.", "info");
        } catch (error) {
            Alert(
                "warn",
                "에러",
                error.response?.data?.root_tree_node_id?.[0] ||
                    error.response?.data?.detail ||
                    "루트 문서 설정에 실패했습니다.",
            );
        }
    };

    const renameSpecificNode = async (node) => {
        if (!node) return;
        const name = window.prompt("이름을 입력하세요.", node.name);
        if (!name || name === node.name) return;
        try {
            await nodeMutation.mutateAsync({
                type: "update",
                nodeId: node.id,
                payload: { name },
            });
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.name?.[0] || "이름 변경에 실패했습니다.");
        }
    };

    const removeSpecificNode = async (node) => {
        if (!node) return;
        if (!window.confirm(`'${node.name}' 항목을 삭제하시겠습니까?`)) return;
        try {
            await nodeMutation.mutateAsync({
                type: "delete",
                nodeId: node.id,
            });
            if (selectedNodeId === node.id) {
                setSelectedNodeId(null);
            }
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "노드 삭제 중 오류가 발생했습니다.");
        }
    };

    const uploadBinaryFile = async (file) => {
        if (!file) return;

        const parentId = selectedNode?.node_type === "folder" ? selectedNode.id : selectedNode?.parent_id || null;

        if (isTextUploadFile(file)) {
            const currentContent = await file.text();
            return nodeMutation.mutateAsync({
                type: "create",
                payload: {
                    name: file.name,
                    parent_id: parentId,
                    node_type: "text",
                    current_content: currentContent,
                },
            });
        }

        const formData = new FormData();
        formData.append("name", file.name);
        formData.append("file", file);
        if (parentId) {
            formData.append("parent_id", parentId);
        }
        return nodeMutation.mutateAsync({ type: "create", payload: formData });
    };

    const uploadBinary = async (source) => {
        const files = Array.isArray(source)
            ? source
            : source?.target?.files
              ? Array.from(source.target.files)
              : source?.dataTransfer?.files
                ? Array.from(source.dataTransfer.files)
                : [];

        if (!files.length) {
            return;
        }

        let uploadedCount = 0;
        let lastError = null;

        try {
            for (const file of files) {
                try {
                    await uploadBinaryFile(file);
                    uploadedCount += 1;
                } catch (error) {
                    lastError = error;
                }
            }

            if (uploadedCount > 0) {
                setToast(
                    uploadedCount === 1 ? "파일이 업로드되었습니다." : `${uploadedCount}개 파일이 업로드되었습니다.`,
                    "info",
                );
            }

            if (lastError) {
                Alert(
                    "warn",
                    "에러",
                    lastError.response?.data?.file?.[0] ||
                        lastError.response?.data?.detail ||
                        "일부 파일 업로드 중 오류가 발생했습니다.",
                );
            }
        } finally {
            if (source?.target) {
                source.target.value = "";
            }
        }
    };

    const scheduleCursorUpdate = (info) => {
        if (!selectedTextNodeId) {
            return;
        }
        pendingCursorPayloadRef.current = {
            ...info,
            node_id: selectedTextNodeId,
            editor_mode: currentEditorMode,
            ts: Date.now(),
        };
        if (cursorBroadcastTimerRef.current) {
            return;
        }
        cursorBroadcastTimerRef.current = window.setTimeout(() => {
            cursorBroadcastTimerRef.current = null;
            if (pendingCursorPayloadRef.current) {
                sendCursorUpdate(pendingCursorPayloadRef.current);
            }
        }, 80);
    };

    const updateSelection = (value, selectionStart) => {
        const info = caretInfo(value, selectionStart);
        setSelection(info);
        scheduleCursorUpdate(info);
    };

    const updateSelectionInfo = (info) => {
        setSelection(info);
        scheduleCursorUpdate(info);
    };

    const updateActiveEditorLine = (lineNumber) => {
        setActiveEditorLine(Math.max(1, lineNumber || 1));
    };

    const jumpToLine = (lineNumber, range = null) => {
        const editorView = codeMirrorRef.current?.view;
        if (!editorView) return;
        const totalLines = editorView.state.doc.lines || 1;
        const clampedLine = Math.max(1, Math.min(lineNumber, totalLines));
        const lineInfo = editorView.state.doc.line(clampedLine);
        const lineLength = Math.max(0, lineInfo.to - lineInfo.from);
        let selectionStart = lineInfo.from;
        let selectionEnd = lineInfo.from;

        if (range?.startCol && range?.endCol) {
            const startOffset = Math.max(0, Math.min(lineLength, range.startCol - 1));
            const endOffset = Math.max(startOffset, Math.min(lineLength, range.endCol - 1));
            selectionStart = lineInfo.from + startOffset;
            selectionEnd = lineInfo.from + endOffset;
        }

        editorView.focus();
        editorView.dispatch({
            selection: { anchor: selectionStart, head: selectionEnd },
            scrollIntoView: true,
        });
        updateSelection(editorContent, selectionEnd);
        updateActiveEditorLine(clampedLine);
    };

    const onEditorChange = (nextValue, selectionStart = 0) => {
        latestContentRef.current = nextValue;
        setEditorContent(nextValue);
        const dirty = nextValue !== syncedContentRef.current;
        setIsDirty(dirty);
        setSaveState(dirty ? "dirty" : "saved");
        updateSelection(nextValue, selectionStart);
    };

    const handleEditorModeChange = (nextMode) => {
        if (!selectedTextNodeId) return;
        const availableModes = documentQuery.data?.available_editor_modes || ["code"];
        if (!availableModes.includes(nextMode)) return;
        preferredEditorModesRef.current[selectedTextNodeId] = nextMode;
        setCurrentEditorMode(nextMode);
    };

    const saveLabel =
        {
            idle: "준비",
            dirty: "편집 중",
            saving: "저장 중",
            saved: "저장됨",
            error: "오류",
        }[saveState] || "준비";

    const handleCompile = async () => {
        try {
            await flushCurrentDocument();
            await compileMutation.mutateAsync(manuscriptId);
            setToast("compile 작업을 시작했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "compile 실행에 실패했습니다.");
        }
    };

    const handleCompileAction = () => {
        if (!selectedTextNodeId) {
            return;
        }

        if (hasLintingError) {
            if (firstLintError) {
                const line = Math.max(1, Number(firstLintError.row || 0) + 1);
                const startCol = Math.max(1, Number(firstLintError.column || 0) + 1);
                const endCol = Math.max(startCol, Number(firstLintError.endColumn || firstLintError.column || 0) + 1);

                setPendingLintErrorFocus({
                    nodeId: selectedTextNodeId,
                    line,
                    startCol,
                    endCol,
                });
            }
            handleEditorModeChange("code");
            setRightPanelTab("pdf");
            return;
        }

        handleCompile();
    };

    const handleRenameManuscript = async () => {
        const currentName = manuscriptQuery.data?.display_name || "";
        const nextName = window.prompt("원고 이름을 입력하세요.", currentName);
        if (!nextName) return;
        const trimmedName = nextName.trim();
        if (!trimmedName || trimmedName === currentName) return;

        try {
            await renameManuscriptMutation.mutateAsync({
                id: manuscriptId,
                display_name: trimmedName,
            });
            setToast("원고 이름을 변경했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.display_name?.[0] || "이름 변경에 실패했습니다.");
        }
    };

    const handleDownloadSourceZip = async () => {
        try {
            const response = await downloadManuscriptSourceZip(space_uuid, manuscriptId);
            const disposition = response.headers?.["content-disposition"] || "";
            const utf8NameMatch = disposition.match(/filename\\*=UTF-8''([^;]+)/i);
            const plainNameMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
            const fileName = utf8NameMatch
                ? decodeURIComponent(utf8NameMatch[1])
                : plainNameMatch?.[1] || `manuscript-${manuscriptId}.zip`;
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "소스(zip) 다운로드에 실패했습니다.");
        }
    };

    const handleCreateReview = async (content, anchor = reviewComposerAnchor || currentReviewAnchor) => {
        try {
            const { anchor_type, anchor_node_id, creation_anchor_range } = resolveReviewAnchor({
                anchor,
                selectedNode,
                selection,
                editorView: codeMirrorRef.current?.view,
            });
            if (anchor_type === "document_range" && creation_anchor_range?.op == null) {
                throw new Error("리뷰 범위의 history-ot 좌표를 찾지 못했습니다.");
            }
            const created = await reviewCreateMutation.mutateAsync({
                manuscriptId,
                anchor_type,
                anchor_node_id,
                creation_anchor_range,
                content,
            });
            if (anchor_type === "document_range") {
                syncReviewThreadCommentCreated({
                    currentDoc,
                    editorView: codeMirrorRef.current?.view,
                    threadId: created.id,
                    creationAnchorRange: creation_anchor_range,
                });
            }
            setSelectedReviewThreadId(created.id);
            setReviewComposerAnchor(null);
            await reviewQuery.refetch();
            setToast("리뷰를 생성했습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 생성에 실패했습니다.");
            return false;
        }
    };

    const handleReplyReview = async (threadId, content) => {
        try {
            await reviewMessageMutation.mutateAsync({ manuscriptId, threadId, content });
            await reviewQuery.refetch();
            setToast("리뷰 답글을 등록했습니다.", "info");
            return true;
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "리뷰 답글 등록에 실패했습니다.");
            return false;
        }
    };

    const handleSelectReviewThread = async (threadId, options = {}) => {
        const selectionResult = resolveReviewThreadSelection({
            currentThreadId: selectedReviewThreadId,
            nextThreadId: threadId,
            reviewThreads,
            nodeMap,
            getReviewThreadRange,
        });

        if (selectionResult.type === "close") {
            setSelectedReviewThreadId(selectionResult.selectedReviewThreadId);
            setReviewReplyDraft(selectionResult.reviewReplyDraft);
            setReviewOverlayPosition(selectionResult.reviewOverlayPosition);
            return;
        }

        if (selectionResult.type !== "select") {
            return;
        }

        setRightPanelTab("review");
        setIsRightPanelCollapsed(false);
        setSelectedReviewThreadId(selectionResult.selectedReviewThreadId);

        const openedFromEditor = options?.source === "editor";
        const sameActiveNode = selectionResult.node && Number(selectionResult.node.id) === Number(selectedTextNodeId);

        if (openedFromEditor && sameActiveNode) {
            suppressReviewOverlayDismissRef.current = true;
            return;
        }

        if (!selectionResult.node) {
            return;
        }

        await openNode(selectionResult.node);

        if (selectionResult.pendingReviewRangeFocus) {
            setPendingReviewRangeFocus(selectionResult.pendingReviewRangeFocus);
        }
    };

    const handleResolveReview = async (threadId) => {
        try {
            await reviewResolveMutation.mutateAsync({ manuscriptId, threadId });
            const thread = reviewThreads.find((item) => item.id === threadId);
            if (thread?.anchor_type === "document_range") {
                syncReviewThreadCommentResolved({
                    currentDoc,
                    editorView: codeMirrorRef.current?.view,
                    threadId,
                    resolved: true,
                });
            }
            await reviewQuery.refetch();
            setToast("리뷰를 해결 상태로 변경했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 상태 변경에 실패했습니다.");
        }
    };

    const handleReopenReview = async (threadId) => {
        try {
            await reviewReopenMutation.mutateAsync({ manuscriptId, threadId });
            const thread = reviewThreads.find((item) => item.id === threadId);
            if (thread?.anchor_type === "document_range") {
                syncReviewThreadCommentResolved({
                    currentDoc,
                    editorView: codeMirrorRef.current?.view,
                    threadId,
                    resolved: false,
                });
            }
            await reviewQuery.refetch();
            setToast("리뷰를 다시 열었습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || error.message || "리뷰 상태 변경에 실패했습니다.");
        }
    };

    const registerReviewThreadAnchorElement = (threadId, element) => {
        if (!element) {
            reviewThreadAnchorElementMapRef.current.delete(threadId);
            return;
        }
        reviewThreadAnchorElementMapRef.current.set(threadId, element);
    };

    const closeReviewOverlay = () => {
        const closedState = createReviewOverlayClosedState();
        setSelectedReviewThreadId(closedState.selectedReviewThreadId);
        setReviewReplyDraft(closedState.reviewReplyDraft);
        setReviewOverlayPosition(closedState.reviewOverlayPosition);
    };

    const getSelectedReviewAnchorRect = () => {
        return getReviewThreadAnchorRect({
            selectedReviewThread,
            selectedTextNodeId,
            editorView: codeMirrorRef.current?.view,
            reviewThreadAnchorElementMap: reviewThreadAnchorElementMapRef.current,
            getReviewThreadRange,
        });
    };

    const updateReviewOverlayPosition = () => {
        const anchorRect = getSelectedReviewAnchorRect();
        if (!anchorRect) {
            setReviewOverlayPosition(null);
            return;
        }
        const nextPosition = buildReviewOverlayPosition({
            anchorRect,
            overlayRect: reviewOverlayRef.current?.getBoundingClientRect(),
        });
        if (!nextPosition) {
            setReviewOverlayPosition(null);
            return;
        }

        setReviewOverlayPosition((prev) => {
            if (prev && prev.top === nextPosition.top && prev.left === nextPosition.left) {
                return prev;
            }
            return nextPosition;
        });
    };

    const handleRestoreRevision = async (revisionId) => {
        try {
            await restoreMutation.mutateAsync({ manuscriptId, revisionId });
            setSelectedNodeId(null);
            await Promise.all([manuscriptQuery.refetch(), treeQuery.refetch(), historyQuery.refetch()]);
            setToast("히스토리를 복원했습니다.", "info");
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "히스토리 복원에 실패했습니다.");
        }
    };

    const openReviewComposer = (anchor = null, selectionOverride = null) => {
        const nextState = createReviewComposerOpenState({
            anchor,
            selectedNode,
            selection: selectionOverride || selection,
            editorView: codeMirrorRef.current?.view,
            requestKey: reviewComposerRequestKey,
        });
        setReviewComposerAnchor(nextState.reviewComposerAnchor);
        setRightPanelTab(nextState.rightPanelTab);
        setIsRightPanelCollapsed(nextState.isRightPanelCollapsed);
        setReviewComposerRequestKey(nextState.reviewComposerRequestKey);
    };

    const selectionActionMenuItems = createSelectionActionMenuItems({
        onAddReview: () => {
            const selectionFromMenu = selectionActionMenuState?.selection || null;
            setSelectionActionMenuState(null);
            openReviewComposer(null, selectionFromMenu);
        },
    });

    const handleOpenSelectionActionMenu = (payload) => {
        const nextState = createSelectionActionMenuState({
            payload,
            enabled: selectedNode?.node_type === "text",
        });
        if (!nextState) {
            return;
        }
        setSelection(nextState.selection);
        setSelectionActionMenuState(nextState);
    };

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!dragStateRef.current) return;
            event.preventDefault();
            const { type, startX, startWidth } = dragStateRef.current;
            const delta = event.clientX - startX;

            if (type === "left") {
                const nextWidth = Math.min(420, Math.max(220, startWidth + delta));
                setLeftPanelWidth(nextWidth);
                return;
            }

            if (type === "right") {
                const nextWidth = Math.min(960, Math.max(260, startWidth - delta));
                setRightPanelWidth(nextWidth);
            }
        };

        const handleMouseUp = () => {
            dragStateRef.current = null;
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!titleMenuRef.current?.contains(event.target)) {
                setIsTitleMenuOpen(false);
            }
            if (!treeActionMenuRef.current?.contains(event.target)) {
                setTreeActionMenuNodeId(null);
                setTreeActionMenuPosition(null);
            }
            if (!helpMenuRef.current?.contains(event.target)) {
                setHelpMenuPosition(null);
            }
            if (!headerMenuRef.current?.contains(event.target)) {
                setHeaderMenuState(null);
            }
            if (!editorReviewContextMenuRef.current?.contains(event.target)) {
                setSelectionActionMenuState(null);
            }
            const clickedReviewItem = Array.from(reviewThreadAnchorElementMapRef.current.values()).some((element) =>
                element?.contains?.(event.target),
            );
            if (selectedReviewThreadId && !reviewOverlayRef.current?.contains(event.target) && !clickedReviewItem) {
                closeReviewOverlay();
                return;
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            if (isShortcutModalOpen) {
                setIsShortcutModalOpen(false);
                return;
            }
            if (isSettingsModalOpen) {
                setIsSettingsModalOpen(false);
                return;
            }
            setHelpMenuPosition(null);
            setHeaderMenuState(null);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isShortcutModalOpen, isSettingsModalOpen]);

    const openHelpMenu = (anchorElement, direction = "bottom") => {
        if (!anchorElement) return;
        const rect = anchorElement.getBoundingClientRect();
        const menuWidth = 160;
        const menuHeight = 40;
        const top =
            direction === "right"
                ? Math.min(window.innerHeight - menuHeight - 8, Math.max(8, rect.top))
                : Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
        const left =
            direction === "right"
                ? Math.min(window.innerWidth - menuWidth - 8, rect.right + 6)
                : Math.min(window.innerWidth - menuWidth - 8, Math.max(8, rect.left));
        setHelpMenuPosition({ top, left });
    };

    const openHeaderMenu = (menuKey, anchorElement) => {
        if (!anchorElement) return;
        const rect = anchorElement.getBoundingClientRect();
        const menuWidth = 188;
        const menuHeight = 220;
        const top = Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
        const left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, rect.left));
        setHeaderMenuState((prev) =>
            prev?.key === menuKey
                ? null
                : {
                      key: menuKey,
                      top,
                      left,
                  },
        );
        setHelpMenuPosition(null);
    };

    const dispatchEditorCommand = (type) => {
        setEditorCommand({
            type,
            key: `${type}-${Date.now()}`,
        });
    };

    const shortcutRows = useMemo(
        () => [
            { category: "에디터", key: isMac ? "Cmd + S" : "Ctrl + S", desc: "문서 저장" },
            { category: "에디터", key: isMac ? "Cmd + F" : "Ctrl + F", desc: "검색 패널 열기" },
            { category: "에디터", key: isMac ? "Cmd + Z" : "Ctrl + Z", desc: "실행 취소" },
            {
                category: "에디터",
                key: isMac ? "Cmd + Shift + Z" : "Ctrl + Shift + Z",
                desc: "다시 실행",
            },
            { category: "PDF", key: isMac ? "Cmd + Wheel" : "Ctrl + Wheel", desc: "PDF 확대/축소" },
        ],
        [isMac],
    );

    const runHeaderMenuAction = async (action) => {
        setHeaderMenuState(null);

        if (action === "create-file") {
            await createNode("text");
            return;
        }
        if (action === "create-folder") {
            await createNode("folder");
            return;
        }
        if (action === "upload") {
            fileInputRef.current?.click();
            return;
        }
        if (action === "download-source-zip") {
            await handleDownloadSourceZip();
            return;
        }
        if (action === "save") {
            await flushCurrentDocument();
            return;
        }
        if (action === "undo" || action === "redo" || action === "search") {
            dispatchEditorCommand(action);
            return;
        }
        if (action === "toggle-left-panel") {
            setIsLeftPanelCollapsed((prev) => !prev);
            return;
        }
        if (action === "toggle-right-panel") {
            setIsRightPanelCollapsed((prev) => !prev);
            return;
        }
        if (action === "show-pdf") {
            setRightPanelTab("pdf");
            setIsRightPanelCollapsed(false);
            return;
        }
        if (action === "show-history") {
            setRightPanelTab("history");
            setIsRightPanelCollapsed(false);
            return;
        }
        if (action === "show-review") {
            setRightPanelTab("review");
            setIsRightPanelCollapsed(false);
        }
    };

    const headerMenuItems = {
        file: [
            { id: "create-file", label: "새 파일" },
            { id: "create-folder", label: "새 폴더" },
            { id: "upload", label: "업로드" },
            { id: "download-source-zip", label: "소스(zip) 다운로드" },
        ],
        edit: [
            { id: "save", label: "저장", shortcut: isMac ? "Cmd+S" : "Ctrl+S" },
            { id: "undo", label: "실행 취소", shortcut: isMac ? "Cmd+Z" : "Ctrl+Z" },
            { id: "redo", label: "다시 실행", shortcut: isMac ? "Cmd+Shift+Z" : "Ctrl+Shift+Z" },
            { id: "search", label: "찾기", shortcut: isMac ? "Cmd+F" : "Ctrl+F" },
        ],
        view: [
            { id: "toggle-left-panel", label: isLeftPanelCollapsed ? "좌측 패널 열기" : "좌측 패널 닫기" },
            { id: "toggle-right-panel", label: isRightPanelCollapsed ? "우측 패널 열기" : "우측 패널 닫기" },
            { id: "show-pdf", label: "PDF" },
            { id: "show-history", label: "히스토리" },
            { id: "show-review", label: "리뷰" },
        ],
    };

    const moveNodeToParent = async (node, parentNode) => {
        if (!node) return;
        const parentId = parentNode?.node_type === "folder" ? parentNode.id : parentNode?.parent_id || null;
        if (String(parentId || "") === String(node.parent_id || "")) return;
        try {
            await nodeMutation.mutateAsync({
                type: "update",
                nodeId: node.id,
                payload: { parent_id: parentId },
            });
            setToast("파일 위치를 이동했습니다.", "info");
        } catch (error) {
            Alert(
                "warn",
                "에러",
                error.response?.data?.detail || error.response?.data?.parent_id?.[0] || "파일 이동에 실패했습니다.",
            );
        }
    };

    const runManuscriptSearch = async () => {
        const term = searchQuery.trim();
        if (!term) {
            setSearchResults([]);
            return;
        }

        const runId = Date.now();
        searchRunRef.current = runId;
        setSearchLoading(true);
        try {
            const results = await getManuscriptSearch(space_uuid, manuscriptId, {
                q: term,
                limit: 1000,
            });
            if (searchRunRef.current !== runId) return;
            setSearchResults(results || []);
            setSearchLoading(false);
        } catch (_error) {
            if (searchRunRef.current !== runId) return;
            setSearchResults([]);
            setSearchLoading(false);
        }
    };

    const handleSearchResultSelect = async (result) => {
        const nodeId = result.nodeId ?? result.node_id;
        const node = nodeMap.get(Number(nodeId));
        if (!node) return;
        await openNode(node);
        if (result.line) {
            window.setTimeout(() => {
                jumpToLine(result.line, {
                    startCol: result.startCol ?? result.start_col,
                    endCol: result.endCol ?? result.end_col,
                });
            }, 60);
        }
    };

    const handleCompileLogEntrySelect = async (entry) => {
        const normalizedPath = normalizeCompileLogPath(entry?.file || "");
        const lineNumber = Number.parseInt(entry?.line, 10);

        if (!normalizedPath || !Number.isFinite(lineNumber)) {
            return;
        }

        const matchedNode =
            treeNodes.find((node) => {
                if (node.node_type !== "text") return false;
                const nodePath = normalizeCompileLogPath(node.path_cache || "");
                return nodePath === normalizedPath;
            }) ||
            treeNodes.find((node) => {
                if (node.node_type !== "text") return false;
                const nodePath = normalizeCompileLogPath(node.path_cache || "");
                return nodePath.endsWith(`/${normalizedPath}`) || normalizedPath.endsWith(`/${nodePath}`);
            });

        if (!matchedNode) {
            setToast(`로그 항목의 파일을 찾을 수 없습니다: ${normalizedPath}`, "warning");
            return;
        }

        setRightPanelTab("pdf");
        setIsRightPanelCollapsed(false);
        await openNode(matchedNode);
        setEditorCommand({
            type: "focusLine",
            lineNumber,
            key: `focusLine-${matchedNode.id}-${lineNumber}-${Date.now()}`,
        });
    };

    const handleSaveProjectSettings = async () => {
        try {
            const payload = {};
            if (settingsRootNodeId) {
                payload.root_tree_node_id = Number(settingsRootNodeId);
            }
            if (settingsCompiler) {
                payload.default_compiler = settingsCompiler;
            }
            await manuscriptSettingsMutation.mutateAsync(payload);
            await treeQuery.refetch();
            setToast("프로젝트 설정을 저장했습니다.", "info");
            setIsSettingsModalOpen(false);
        } catch (error) {
            Alert("warn", "에러", error.response?.data?.detail || "설정 저장에 실패했습니다.");
        }
    };

    const startResize = (type, startWidth) => (event) => {
        event.preventDefault();
        dragStateRef.current = {
            type,
            startX: event.clientX,
            startWidth,
        };
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
    };

    const renderTreeNode = (node, depth = 0) => {
        const isFolder = node.node_type === "folder";
        const isExpanded = expandedFolderIds[node.id] ?? true;
        const isSelected = selectedNodeId === node.id;
        const isDragging = draggedTreeNodeId === node.id;
        const isDropTarget = dropTargetNodeId === node.id;
        return (
            <React.Fragment key={node.id}>
                <TreeIndent $depth={depth}>
                    <TreeItem
                        $selected={isSelected || isDropTarget}
                        $dragging={isDragging}
                        data-selected={isSelected ? "true" : "false"}
                        role="button"
                        tabIndex={0}
                        onClick={() => openNode(node)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openNode(node);
                            }
                        }}
                        draggable
                        onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = "move";
                            setDraggedTreeNodeId(node.id);
                            setDropTargetNodeId(null);
                        }}
                        onDragEnd={() => {
                            setDraggedTreeNodeId(null);
                            setDropTargetNodeId(null);
                        }}
                        onDragOver={(event) => {
                            if (draggedTreeNodeId == null || draggedTreeNodeId === node.id) return;
                            event.preventDefault();
                            setDropTargetNodeId(node.id);
                        }}
                        onDragLeave={() => {
                            if (dropTargetNodeId === node.id) {
                                setDropTargetNodeId(null);
                            }
                        }}
                        onDrop={async (event) => {
                            event.preventDefault();
                            const draggedNode = nodeMap.get(draggedTreeNodeId);
                            setDropTargetNodeId(null);
                            setDraggedTreeNodeId(null);
                            await moveNodeToParent(draggedNode, node);
                        }}
                    >
                        {isFolder ? (
                            <Icon
                                name={isExpanded ? "arrowDown" : "arrowRight"}
                                size={"12px"}
                                color={isSelected ? "#0f4c81" : "#6a7f97"}
                            />
                        ) : (
                            <span style={{ width: 12, flex: "0 0 12px" }} />
                        )}
                        {isFolder ? (
                            <Icon
                                name={isExpanded ? "folderDownward" : "folderRightward"}
                                size={"12px"}
                                color={isSelected ? "#0f4c81" : "#6a7f97"}
                            />
                        ) : null}
                        {!isFolder ? (
                            <Icon name={"file"} size={"12px"} color={isSelected ? "#0f4c81" : "#6a7f97"} />
                        ) : null}
                        <TreeLabel $root={node.is_root}>{node.name}</TreeLabel>
                        {isSelected ? (
                            <TreeActions>
                                <TreeActionMenuWrap ref={treeActionMenuNodeId === node.id ? treeActionMenuRef : null}>
                                    <TreeActionButton
                                        type="button"
                                        aria-label={`${node.name} 작업 메뉴`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const buttonRect = event.currentTarget.getBoundingClientRect();
                                            const estimatedMenuHeight = 110;
                                            const estimatedMenuWidth = 132;
                                            const opensUpward =
                                                window.innerHeight - buttonRect.bottom < estimatedMenuHeight &&
                                                buttonRect.top > window.innerHeight - buttonRect.bottom;
                                            const top = opensUpward
                                                ? Math.max(8, buttonRect.top - estimatedMenuHeight - 4)
                                                : Math.min(
                                                      window.innerHeight - estimatedMenuHeight - 8,
                                                      buttonRect.bottom + 4,
                                                  );
                                            const left = Math.min(
                                                window.innerWidth - estimatedMenuWidth - 8,
                                                Math.max(8, buttonRect.right - estimatedMenuWidth),
                                            );
                                            setTreeActionMenuPosition({ top, left });
                                            setTreeActionMenuNodeId((prev) => (prev === node.id ? null : node.id));
                                        }}
                                    >
                                        <span style={{ color: "#56708a", fontSize: 12, lineHeight: 1 }}>…</span>
                                    </TreeActionButton>
                                </TreeActionMenuWrap>
                            </TreeActions>
                        ) : null}
                    </TreeItem>
                </TreeIndent>
                {isFolder && isExpanded ? node.children.map((child) => renderTreeNode(child, depth + 1)) : null}
            </React.Fragment>
        );
    };

    return (
        <Page>
            <HtmlHead title={manuscriptQuery.data?.display_name || "논문작성"} />
            <Header>
                <HeaderLeft>
                    <HeaderBrand>
                        <HeaderLogoLink to="/" reloadDocument>
                            <Image name={"headerLogo"} />
                        </HeaderLogoLink>
                    </HeaderBrand>
                    <ToolbarMenu aria-label="editor menu">
                        <ToolbarMenuItem
                            type="button"
                            aria-expanded={headerMenuState?.key === "file"}
                            onClick={(event) => openHeaderMenu("file", event.currentTarget)}
                        >
                            파일
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            aria-expanded={headerMenuState?.key === "edit"}
                            onClick={(event) => openHeaderMenu("edit", event.currentTarget)}
                        >
                            편집
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            aria-expanded={headerMenuState?.key === "view"}
                            onClick={(event) => openHeaderMenu("view", event.currentTarget)}
                        >
                            보기
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            onClick={(event) => {
                                setHeaderMenuState(null);
                                setIsShortcutModalOpen(false);
                                openHelpMenu(event.currentTarget, "bottom");
                            }}
                        >
                            도움말
                        </ToolbarMenuItem>
                    </ToolbarMenu>
                </HeaderLeft>
                <HeaderCenter>
                    <HeaderTitleWrap>
                        <HeaderTitle>{manuscriptQuery.data?.display_name || "논문작성"}</HeaderTitle>
                    </HeaderTitleWrap>
                    <HeaderTitleMenuWrap ref={titleMenuRef}>
                        <TitleMenuButton type="button" onClick={() => setIsTitleMenuOpen((prev) => !prev)}>
                            <Icon name={"arrowDown"} size={"12px"} color={"#41586f"} />
                        </TitleMenuButton>
                        {isTitleMenuOpen ? (
                            <TitleMenu>
                                <TitleMenuItem
                                    type="button"
                                    onClick={() => {
                                        setIsTitleMenuOpen(false);
                                        handleRenameManuscript();
                                    }}
                                >
                                    이름 바꾸기
                                </TitleMenuItem>
                                <TitleMenuItem
                                    type="button"
                                    onClick={() => {
                                        setIsTitleMenuOpen(false);
                                        handleDownloadSourceZip();
                                    }}
                                >
                                    소스(zip) 다운로드
                                </TitleMenuItem>
                            </TitleMenu>
                        ) : null}
                    </HeaderTitleMenuWrap>
                </HeaderCenter>
                <HeaderActions>
                    <Status $bg={isConnected ? "#e8faf2" : "#eef2f7"} $color={isConnected ? "#0f8f6f" : "#607289"}>
                        {isConnected ? "실시간 연결" : "연결 끊김"}
                    </Status>
                    {activePresenceParticipants.length ? (
                        <PresenceGroup aria-label="현재 편집 중인 멤버">
                            {activePresenceParticipants.slice(0, 6).map((participant) => {
                                const label = participant?.name || "Member";
                                return (
                                    <Tooltip
                                        key={participant.space_member_id || participant.user_id || label}
                                        message={label}
                                    >
                                        <PresenceAvatar participant={participant} />
                                    </Tooltip>
                                );
                            })}
                        </PresenceGroup>
                    ) : null}
                </HeaderActions>
            </Header>

            <Workspace
                $leftPanelWidth={leftPanelWidth}
                $rightPanelWidth={rightPanelWidth}
                $leftPanelCollapsed={isLeftPanelCollapsed}
                $rightPanelCollapsed={isRightPanelCollapsed}
            >
                <ManuscriptLeftRail
                    activePanel={leftPanelTab}
                    onSelect={(panel) => {
                        setLeftPanelTab(panel);
                        setIsLeftPanelCollapsed(false);
                    }}
                    onSettings={() => {
                        setHelpMenuPosition(null);
                        setIsShortcutModalOpen(false);
                        setIsSettingsModalOpen(true);
                    }}
                    onHelp={(event) => {
                        setIsShortcutModalOpen(false);
                        openHelpMenu(event.currentTarget, "right");
                    }}
                />
                <ManuscriptLeftPanel
                    activePanel={leftPanelTab}
                    roots={roots}
                    renderTreeNode={renderTreeNode}
                    selectedNode={selectedNode}
                    createNode={createNode}
                    fileInputRef={fileInputRef}
                    uploadBinary={uploadBinary}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSearchSubmit={runManuscriptSearch}
                    searchLoading={searchLoading}
                    searchResults={searchResults}
                    onSearchResultSelect={handleSearchResultSelect}
                    outlineItems={documentOutline}
                    outlineEnabled={selectedNode?.node_type === "text"}
                    activeOutlineLine={activeEditorLine}
                    activeOutlineIds={activeOutlineIds}
                    onOutlineSelect={jumpToLine}
                    isCollapsed={isLeftPanelCollapsed}
                />
                <ResizeHandle
                    onMouseDown={isLeftPanelCollapsed ? undefined : startResize("left", leftPanelWidth)}
                    $hidden={isLeftPanelCollapsed}
                    onClick={isLeftPanelCollapsed ? () => setIsLeftPanelCollapsed(false) : undefined}
                >
                    <ResizeHandleToggle
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsLeftPanelCollapsed((prev) => !prev);
                        }}
                        aria-label={isLeftPanelCollapsed ? "좌측 패널 열기" : "좌측 패널 닫기"}
                    >
                        {isLeftPanelCollapsed ? ">" : "<"}
                    </ResizeHandleToggle>
                </ResizeHandle>
                <ManuscriptEditorPanel
                    selectedNode={selectedNode}
                    manuscriptTreeData={treeNodes}
                    setToast={setToast}
                    uploadBinaryFile={uploadBinaryFile}
                    saveLabel={saveLabel}
                    onSave={flushCurrentDocument}
                    saveDisabled={!selectedTextNodeId}
                    selection={selection}
                    editorContent={editorContent}
                    onEditorChange={onEditorChange}
                    onSelectionChange={updateSelectionInfo}
                    onActiveLineChange={updateActiveEditorLine}
                    editorRef={codeMirrorRef}
                    currentVersion={currentVersion}
                    currentEditorMode={currentEditorMode}
                    availableEditorModes={documentQuery.data?.available_editor_modes || ["code"]}
                    onEditorModeChange={handleEditorModeChange}
                    currentLocationPath={activeOutlinePath}
                    onLocationSelect={jumpToLine}
                    onOpenSelectionActionMenu={handleOpenSelectionActionMenu}
                    onSelectReviewThread={handleSelectReviewThread}
                    externalCommand={editorCommand}
                    realtimeExtension={realtimeExtension}
                    remoteCursors={activeRemoteCursors}
                    compileLogAnnotations={compileLogAnnotationsByNodeId[selectedTextNodeId] || []}
                    compiling={compileInProgress}
                    editedSinceCompileStarted={isDirty}
                    syntaxValidation={true}
                />
                <ResizeHandle
                    onMouseDown={isRightPanelCollapsed ? undefined : startResize("right", rightPanelWidth)}
                    $hidden={isRightPanelCollapsed}
                    onClick={isRightPanelCollapsed ? () => setIsRightPanelCollapsed(false) : undefined}
                >
                    <ResizeHandleToggle
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsRightPanelCollapsed((prev) => !prev);
                        }}
                        aria-label={isRightPanelCollapsed ? "우측 패널 열기" : "우측 패널 닫기"}
                    >
                        {isRightPanelCollapsed ? "<" : ">"}
                    </ResizeHandleToggle>
                </ResizeHandle>
                <ManuscriptRightPanel
                    activePanel={rightPanelTab}
                    latestCompile={latestCompile}
                    pdfArtifact={displayedPdfArtifact}
                    pdfRevisionSeq={displayedPdfRevisionSeq}
                    logArtifact={logArtifact}
                    compileRevisionSeq={latestCompileRevisionSeq}
                    compileLogRaw={compileLogRaw}
                    compileLogEntries={compileLogEntries}
                    compileLogLoading={compileLogLoading}
                    compileLogLoadError={compileLogLoadError}
                    onCompile={handleCompileAction}
                    onLogEntrySelect={handleCompileLogEntrySelect}
                    compileLoading={compileMutation.isLoading}
                    compileDisabled={!selectedTextNodeId}
                    compileHasLintError={hasLintingError}
                    compileDisabledReason={compileDisabledReason}
                    historyItems={historyItems}
                    restoreMutation={restoreMutation}
                    handleRestoreRevision={handleRestoreRevision}
                    saveLabel={saveLabel}
                    reviewThreads={reviewThreads}
                    selectedReviewThreadId={selectedReviewThreadId}
                    selectedReviewThread={selectedReviewThread}
                    onSelectReviewThread={handleSelectReviewThread}
                    onReviewThreadMount={registerReviewThreadAnchorElement}
                    createReviewLoading={reviewCreateMutation.isLoading}
                    onOpenReviewComposer={openReviewComposer}
                    onCreateReview={handleCreateReview}
                    currentReviewAnchor={reviewComposerAnchor || currentReviewAnchor}
                    openReviewComposerRequestKey={reviewComposerRequestKey}
                    isCollapsed={isRightPanelCollapsed}
                    reviewReplyDraft={reviewReplyDraft}
                    onReviewReplyDraftChange={setReviewReplyDraft}
                    onReplyReview={async (threadId, content) => {
                        const created = await handleReplyReview(threadId, content);
                        if (created !== false) {
                            setReviewReplyDraft("");
                        }
                    }}
                    onResolveReview={handleResolveReview}
                    onReopenReview={handleReopenReview}
                    reviewMessageLoading={reviewMessageMutation.isLoading}
                    reviewResolveLoading={reviewResolveMutation.isLoading}
                    reviewReopenLoading={reviewReopenMutation.isLoading}
                />
                <ManuscriptRightRail
                    activePanel={rightPanelTab}
                    onSelect={(panel) => {
                        setRightPanelTab(panel);
                        setIsRightPanelCollapsed(false);
                    }}
                />
            </Workspace>
            {treeActionMenuNodeId && treeActionMenuPosition
                ? createPortal(
                      <TreeActionMenu
                          ref={treeActionMenuRef}
                          $top={treeActionMenuPosition.top}
                          $left={treeActionMenuPosition.left}
                      >
                          <TreeActionMenuItem
                              type="button"
                              onClick={() => {
                                  const node = nodeMap.get(Number(treeActionMenuNodeId));
                                  setTreeActionMenuNodeId(null);
                                  setTreeActionMenuPosition(null);
                                  renameSpecificNode(node);
                              }}
                          >
                              이름 바꾸기
                          </TreeActionMenuItem>
                          {nodeMap.get(Number(treeActionMenuNodeId))?.node_type === "text" &&
                          nodeMap.get(Number(treeActionMenuNodeId))?.name?.toLowerCase().endsWith(".tex") ? (
                              <TreeActionMenuItem
                                  type="button"
                                  onClick={() => {
                                      const node = nodeMap.get(Number(treeActionMenuNodeId));
                                      setTreeActionMenuNodeId(null);
                                      setTreeActionMenuPosition(null);
                                      setRootNode(node);
                                  }}
                              >
                                  루트로 지정
                              </TreeActionMenuItem>
                          ) : null}
                          <TreeActionMenuItem
                              type="button"
                              onClick={() => {
                                  const node = nodeMap.get(Number(treeActionMenuNodeId));
                                  setTreeActionMenuNodeId(null);
                                  setTreeActionMenuPosition(null);
                                  removeSpecificNode(node);
                              }}
                          >
                              삭제
                          </TreeActionMenuItem>
                      </TreeActionMenu>,
                      document.body,
                  )
                : null}
            {selectedReviewThread && reviewOverlayPosition
                ? createPortal(
                      <ReviewOverlayShell
                          ref={reviewOverlayRef}
                          data-testid="review-overlay"
                          style={{
                              top: reviewOverlayPosition.top,
                              left: reviewOverlayPosition.left,
                          }}
                      >
                          <ReviewThreadDetail
                              thread={selectedReviewThread}
                              variant="overlay"
                              replyDraft={reviewReplyDraft}
                              onReplyDraftChange={setReviewReplyDraft}
                              onReply={async () => {
                                  const created = await handleReplyReview(selectedReviewThread.id, reviewReplyDraft);
                                  if (created !== false) {
                                      setReviewReplyDraft("");
                                  }
                              }}
                              onResolve={() => handleResolveReview(selectedReviewThread.id)}
                              onReopen={() => handleReopenReview(selectedReviewThread.id)}
                              onClose={closeReviewOverlay}
                              replyLoading={reviewMessageMutation.isLoading}
                              resolveLoading={reviewResolveMutation.isLoading}
                              reopenLoading={reviewReopenMutation.isLoading}
                          />
                      </ReviewOverlayShell>,
                      document.body,
                  )
                : null}
            {selectionActionMenuState
                ? createPortal(
                      <EditorContextMenu
                          ref={editorReviewContextMenuRef}
                          style={{
                              top: selectionActionMenuState.position.top,
                              left: selectionActionMenuState.position.left,
                          }}
                      >
                          {selectionActionMenuItems.map((item) => (
                              <EditorContextMenuItem key={item.id} type="button" onClick={item.onSelect}>
                                  <Icon name={item.icon} size={"12px"} color={item.iconColor} />
                                  {item.label}
                              </EditorContextMenuItem>
                          ))}
                      </EditorContextMenu>,
                      document.body,
                  )
                : null}
            {headerMenuState
                ? createPortal(
                      <HeaderDropdownMenu ref={headerMenuRef} $top={headerMenuState.top} $left={headerMenuState.left}>
                          {(headerMenuItems[headerMenuState.key] || []).map((item) => (
                              <HeaderDropdownItem
                                  key={item.id}
                                  type="button"
                                  onClick={() => {
                                      runHeaderMenuAction(item.id);
                                  }}
                              >
                                  <span>{item.label}</span>
                                  {item.shortcut ? <span>{item.shortcut}</span> : null}
                              </HeaderDropdownItem>
                          ))}
                      </HeaderDropdownMenu>,
                      document.body,
                  )
                : null}
            {helpMenuPosition
                ? createPortal(
                      <HelpDropdownMenu ref={helpMenuRef} $top={helpMenuPosition.top} $left={helpMenuPosition.left}>
                          <HelpDropdownItem
                              type="button"
                              onClick={() => {
                                  setHelpMenuPosition(null);
                                  setIsShortcutModalOpen(true);
                              }}
                          >
                              <span>키보드 단축키</span>
                              <Icon name={"arrowRight"} size={"11px"} />
                          </HelpDropdownItem>
                      </HelpDropdownMenu>,
                      document.body,
                  )
                : null}
            {isShortcutModalOpen ? (
                <Overlay
                    onClick={() => {
                        setIsShortcutModalOpen(false);
                    }}
                >
                    <Dialog
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <DialogHeader>
                            <div>키보드 단축키</div>
                            <ActionButton
                                type="button"
                                onClick={() => {
                                    setIsShortcutModalOpen(false);
                                }}
                            >
                                닫기
                            </ActionButton>
                        </DialogHeader>
                        <DialogBody>
                            <ShortcutTable>
                                <ShortcutCell $header>영역</ShortcutCell>
                                <ShortcutCell $header>키</ShortcutCell>
                                <ShortcutCell $header $last>
                                    기능
                                </ShortcutCell>
                                {shortcutRows.map((row, index) => (
                                    <React.Fragment key={`${row.category}-${row.key}-${index}`}>
                                        <ShortcutCell>{row.category}</ShortcutCell>
                                        <ShortcutCell>{row.key}</ShortcutCell>
                                        <ShortcutCell $last>{row.desc}</ShortcutCell>
                                    </React.Fragment>
                                ))}
                            </ShortcutTable>
                        </DialogBody>
                    </Dialog>
                </Overlay>
            ) : null}
            {isSettingsModalOpen ? (
                <Overlay
                    onClick={() => {
                        setIsSettingsModalOpen(false);
                    }}
                >
                    <Dialog
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <DialogHeader>
                            <div>프로젝트 설정</div>
                            <ActionButton
                                type="button"
                                onClick={() => {
                                    setIsSettingsModalOpen(false);
                                }}
                            >
                                닫기
                            </ActionButton>
                        </DialogHeader>
                        <DialogBody>
                            <SettingsTabs>
                                <SettingsTabButton
                                    type="button"
                                    $active={settingsTab === "compiler"}
                                    onClick={() => setSettingsTab("compiler")}
                                >
                                    컴파일러
                                </SettingsTabButton>
                            </SettingsTabs>
                            {settingsTab === "compiler" ? (
                                <SettingsForm>
                                    <SettingsRow>
                                        <SettingsLabel>주 문서</SettingsLabel>
                                        <SettingsSelect
                                            value={settingsRootNodeId}
                                            onChange={(event) => setSettingsRootNodeId(event.target.value)}
                                        >
                                            {rootCandidateNodes.length ? (
                                                rootCandidateNodes.map((node) => (
                                                    <option key={node.id} value={String(node.id)}>
                                                        {node.path_cache}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="">선택 가능한 .tex 문서 없음</option>
                                            )}
                                        </SettingsSelect>
                                        <SettingsHint>
                                            {rootCandidateNodes.length
                                                ? ".tex 문서만 주 문서로 선택할 수 있습니다."
                                                : "먼저 .tex 문서를 생성한 뒤 주 문서를 설정할 수 있습니다."}
                                        </SettingsHint>
                                    </SettingsRow>
                                    <SettingsRow>
                                        <SettingsLabel>컴파일러</SettingsLabel>
                                        <SettingsSelect
                                            value={settingsCompiler}
                                            onChange={(event) => setSettingsCompiler(event.target.value)}
                                        >
                                            {compilerOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </SettingsSelect>
                                    </SettingsRow>
                                    <Actions>
                                        <ActionButton
                                            type="button"
                                            onClick={handleSaveProjectSettings}
                                            disabled={
                                                nodeMutation.isLoading ||
                                                manuscriptSettingsMutation.isLoading ||
                                                !rootCandidateNodes.length
                                            }
                                            $primary
                                        >
                                            저장
                                        </ActionButton>
                                    </Actions>
                                </SettingsForm>
                            ) : null}
                        </DialogBody>
                    </Dialog>
                </Overlay>
            ) : null}
        </Page>
    );
}

export default ManuscriptEditorPage;
